import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { PostbackToken } from '../../models/PostbackToken';
import { AuthService } from './auth.service';

/**
 * Anexa o token do cliente à URL de postback (CU-86ak8p7w0).
 *
 * Sem token a URL sai como sempre saiu: conta antiga não é obrigada a usá-lo, e
 * um painel que não conseguiu buscar o token não pode entregar URL quebrada.
 */
export const withPostbackToken = (url: string, token?: string | null): string => {
  if (!token) {
    return url;
  }

  const separator = url.includes('?') ? '&' : '?';

  return `${url}${separator}token=${encodeURIComponent(token)}`;
};

export const integrationPostbackUrl = (integrationId: string, token?: string | null): string =>
  withPostbackToken(`${environment.postbackUrl}/${integrationId}`, token);

export const listPostbackUrl = (listId: string, token?: string | null): string =>
  withPostbackToken(`${environment.postbackUrl}/lists/${listId}/add-lead`, token);

@Injectable({ providedIn: 'root' })
export class PostbackTokenService {

  private resolved: { userId: string; token: PostbackToken } = null;
  private pending: { userId: string; request$: Observable<PostbackToken | null> } = null;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {
    // Simular outra conta ou trocar de usuário troca o dono das URLs.
    this.authService.userChanged.subscribe(() => this.invalidate());
  }

  /**
   * Token da conta em uso — a simulada, quando houver. Uma requisição por conta
   * na sessão. Falha devolve `null` e NÃO fica em cache: a próxima tela tenta de
   * novo em vez de seguir sem token até o logout.
   */
  get(): Observable<PostbackToken | null> {
    const userId = this.currentUserId();

    if (this.resolved?.userId === userId) {
      return of(this.resolved.token);
    }

    if (this.pending?.userId === userId) {
      return this.pending.request$;
    }

    const request$ = this.http.get<PostbackToken>(`${environment.apiUrl}/postback-token`).pipe(
      map(response => (response?.token ? response : null)),
      tap(token => {
        // Resposta que chega depois de trocar de conta pertence à conta anterior.
        if (token && this.pending?.request$ === request$) {
          this.resolved = { userId, token };
        }
      }),
      catchError(() => of(null)),
      finalize(() => {
        if (this.pending?.request$ === request$) {
          this.pending = null;
        }
      }),
      shareReplay(1)
    );

    this.pending = { userId, request$ };

    return request$;
  }

  /** Último token já carregado da conta em uso, sem disparar requisição. */
  peek(): PostbackToken | null {
    return this.resolved?.userId === this.currentUserId() ? this.resolved.token : null;
  }

  /**
   * Gera token novo. O backend passa a recusar as URLs com o token anterior, então
   * o cache é trocado na hora — nenhuma tela pode seguir mostrando a URL antiga.
   */
  regenerate(): Observable<PostbackToken> {
    const userId = this.currentUserId();

    return this.http.post<PostbackToken>(`${environment.apiUrl}/postback-token/regenerate`, {}).pipe(
      tap(token => {
        this.invalidate();
        this.resolved = { userId, token };
      })
    );
  }

  invalidate(): void {
    this.resolved = null;
    this.pending = null;
  }

  private currentUserId(): string {
    return this.authService.getUser()?.id ?? null;
  }
}
